import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { Modal } from 'tio-ui/components/modal';
import { Button } from 'tio-ui/components/buttons';
import { Input, RadioGroup, Form } from 'tio-ui/components/forms';
import { on } from '@ember/modifier';
import { eq } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import * as v from 'valibot';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import type IntlService from 'ember-intl/services/intl';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type EmployeeModel from 'tio-common/models/employee';
import type EmployeeAdapter from 'tio-employee/adapters/employee';
import type { FormResultData } from 'tio-ui/components/forms';

interface InviteFamilyMemberModalSignature {
  Args: {
    show: boolean;
    employee: EmployeeModel;
    onClose: () => void;
    onSuccess: () => void;
  };
}

const RELATIONSHIP_OPTIONS = [
  { key: 'CHILD', label: 'profile.family.modal.relationship_child' },
  { key: 'SPOUSE', label: 'profile.family.modal.relationship_spouse' },
  { key: 'PARTNER', label: 'profile.family.modal.relationship_partner' },
];

type ModalStep = 'form' | 'terms' | 'success';

type InviteFormData = {
  firstName: string;
  lastName: string;
  email: string;
  birthDate: string;
  relationship: string;
};

const formSchema = v.object({
  firstName: v.pipe(v.string('First Name is Required'), v.nonEmpty('First Name is Required')),
  lastName: v.pipe(v.string('Last Name is Required'), v.nonEmpty('Last Name is Required')),
  email: v.pipe(v.string('Invalid Email Address'), v.email('Invalid Email Address')),
  birthDate: v.pipe(v.string('Birth Date is Required'), v.nonEmpty('Birth Date is Required')),
  relationship: v.pipe(
    v.string('Relationship is Required'),
    v.nonEmpty('Relationship is Required')
  ),
});

export default class InviteFamilyMemberModal extends Component<InviteFamilyMemberModalSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;

  @tracked formData: InviteFormData = {
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    relationship: '',
  };

  @tracked currentStep: ModalStep = 'form';
  @tracked isSubmitting = false;
  @tracked hasAttemptedSubmit = false;
  @tracked errorMessage = '';
  @tracked formValidationErrors: Record<string, string[]> = {};

  relationshipOptions = RELATIONSHIP_OPTIONS;

  resetForm = () => {
    this.formData = {
      firstName: '',
      lastName: '',
      email: '',
      birthDate: '',
      relationship: '',
    };
    this.errorMessage = '';
    this.isSubmitting = false;
    this.hasAttemptedSubmit = false;
    this.currentStep = 'form';
    this.formValidationErrors = {};
  };

  handleClose = () => {
    if (this.currentStep === 'success') {
      this.resetForm();
      this.args.onSuccess();
    } else {
      this.resetForm();
      this.args.onClose();
    }
  };

  validateField = (data: InviteFormData) => {
    const result = v.safeParse(formSchema, data);

    if (result.success) {
      this.formValidationErrors = {};
      return true;
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.formValidationErrors = validationErrors;
      return false;
    }
  };

  onFormChange = (result: FormResultData<Record<string, any>>) => {
    const newData = { ...result.data } as InviteFormData;

    // Handle Date object from Input type="date"
    if ((newData.birthDate as unknown) instanceof Date) {
      const date = newData.birthDate as unknown as Date;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      newData.birthDate = `${year}-${month}-${day}`;
    }

    this.formData = {
      ...this.formData,
      ...newData,
    };

    if (this.hasAttemptedSubmit) {
      this.validateField(this.formData);
    }
  };

  onRelationshipChange = (value: string) => {
    this.formData = {
      ...this.formData,
      relationship: value,
    };
    if (this.hasAttemptedSubmit) {
      this.validateField(this.formData);
    }
  };

  handleFormSubmit = (_result: FormResultData<Record<string, any>>, event: SubmitEvent) => {
    event.preventDefault();
    this.proceedToTerms();
  };

  proceedToTerms = () => {
    this.hasAttemptedSubmit = true;
    const isValid = this.validateField(this.formData);
    if (!isValid) return;
    this.currentStep = 'terms';
  };

  acceptTerms = async () => {
    this.isSubmitting = true;
    this.errorMessage = '';

    try {
      const adapter = this.store.adapterFor('employee') as unknown as EmployeeAdapter;
      const employeeId = this.args.employee.id;

      if (!employeeId) {
        throw new Error('Employee ID is required');
      }

      // Call custom adapter method for add-employee-dependent endpoint
      await adapter.addEmployeeDependent(employeeId, {
        first_name: this.formData.firstName,
        last_name: this.formData.lastName,
        email: this.formData.email,
        birth_date: this.formData.birthDate,
        relationship: this.formData.relationship,
      });

      this.currentStep = 'success';
    } catch (error) {
      this.errorMessage = this.intl.t('profile.family.modal.error_message');
      this.currentStep = 'form'; // Go back to form on error
      console.error('Error inviting family member:', error);
    } finally {
      this.isSubmitting = false;
    }
  };

  declineTerms = () => {
    this.currentStep = 'form';
  };

  <template>
    <Modal @isOpen={{@show}} @onClose={{this.handleClose}} @size="lg" as |m|>
      {{#if (eq this.currentStep "form")}}
        {{! Step 1: Contact Form }}
        <m.Header>
          <div class="text-center">
            <h2 class="text-2xl font-semibold text-purple-900">{{t
                "profile.family.modal.title"
              }}</h2>
            <p class="text-gray-600 mt-2">{{t "profile.family.modal.subtitle"}}</p>
          </div>
        </m.Header>
        <m.Body>
          <Form
            @onChange={{this.onFormChange}}
            @onSubmit={{this.handleFormSubmit}}
            class="space-y-4"
          >
            {{#if this.errorMessage}}
              <div class="p-3 bg-red-50 border border-red-200 rounded text-red-800">
                {{this.errorMessage}}
              </div>
            {{/if}}

            {{! Two-column layout for name/email fields }}
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                @label={{t "profile.family.modal.first_name"}}
                @value={{this.formData.firstName}}
                @errors={{errorsForValibotField "firstName" schemaErrors=this.formValidationErrors}}
                name="firstName"
                placeholder="Bessy"
                required
              />

              <Input
                @label={{t "profile.family.modal.last_name"}}
                @value={{this.formData.lastName}}
                @errors={{errorsForValibotField "lastName" schemaErrors=this.formValidationErrors}}
                name="lastName"
                placeholder="Doe"
                required
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                @label={{t "profile.family.modal.email"}}
                @value={{this.formData.email}}
                @errors={{errorsForValibotField "email" schemaErrors=this.formValidationErrors}}
                name="email"
                type="email"
                placeholder="Bessydoe@email.com"
                required
              />

              <Input
                @label={{t "profile.family.modal.birth_date"}}
                @value={{this.formData.birthDate}}
                @errors={{errorsForValibotField "birthDate" schemaErrors=this.formValidationErrors}}
                name="birthDate"
                type="date"
                required
              />
            </div>

            {{! Radio button group for relationship }}
            <div class="mt-4">
              <RadioGroup
                @label={{t "profile.family.modal.relationship_question"}}
                @value={{this.formData.relationship}}
                @errors={{errorsForValibotField
                  "relationship"
                  schemaErrors=this.formValidationErrors
                }}
                @orientation="horizontal"
                @onChange={{this.onRelationshipChange}}
                as |Radio|
              >
                {{#each this.relationshipOptions as |option|}}
                  <Radio @value={{option.key}} @label={{t option.label}} name="relationship" />
                {{/each}}
              </RadioGroup>
            </div>
          </Form>
        </m.Body>
        <m.Footer>
          <div class="flex justify-between items-center w-full">
            <Button
              @appearance="minimal"
              @intent="primary"
              @label={{t "profile.family.modal.cancel"}}
              {{on "click" this.handleClose}}
            />
            <Button
              @intent="primary"
              @label={{t "profile.family.modal.submit"}}
              {{on "click" this.proceedToTerms}}
            />
          </div>
        </m.Footer>
      {{/if}}

      {{#if (eq this.currentStep "terms")}}
        {{! Step 2: Terms Acceptance }}
        <m.Header>{{t "profile.family.modal.terms_title"}}</m.Header>
        <m.Body>
          <div class="prose prose-sm max-w-none">
            <p>
              {{t "profile.family.modal.terms_description"}}
            </p>
            <ul>
              <li>{{t "profile.family.modal.terms_list_1"}}</li>
              <li>{{t "profile.family.modal.terms_list_2"}}</li>
              <li>{{t "profile.family.modal.terms_list_3" htmlSafe=true}}</li>
              <li>{{t "profile.family.modal.terms_list_4"}}</li>
              <li>{{t "profile.family.modal.terms_list_5"}}</li>
            </ul>
          </div>
        </m.Body>
        <m.Footer>
          <div class="flex justify-between items-center w-full">
            <Button
              @appearance="minimal"
              @intent="primary"
              @label={{t "profile.family.modal.decline"}}
              {{on "click" this.declineTerms}}
            />
            <Button
              @intent="primary"
              @label={{t "profile.family.modal.accept"}}
              @isRunning={{this.isSubmitting}}
              {{on "click" this.acceptTerms}}
            />
          </div>
        </m.Footer>
      {{/if}}

      {{#if (eq this.currentStep "success")}}
        {{! Step 3: Success Message }}
        <m.Header>
          <h2 class="text-2xl font-semibold text-center">{{t
              "profile.family.modal.success_title"
            }}</h2>
        </m.Header>
        <m.Body>
          <div class="text-center">
            <p class="text-gray-700 mb-6">
              {{t
                "profile.family.modal.success_message"
                firstName=this.formData.firstName
                lastName=this.formData.lastName
              }}
            </p>
          </div>
        </m.Body>
        <m.Footer>
          <div class="flex justify-end w-full">
            <Button
              @intent="primary"
              @label={{t "profile.family.modal.close"}}
              {{on "click" this.handleClose}}
            />
          </div>
        </m.Footer>
      {{/if}}
    </Modal>
  </template>
}
